import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, Crown, Gift } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | gift | timeout | error
  const [giftCode, setGiftCode] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.gift_code) {
            setGiftCode(data.gift_code);
            setState("gift");
          } else {
            await refreshUser();
            setState("paid");
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        /* retry */
      }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0b0c10] text-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#12161f] p-10 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin cyan-text mb-4" />
            <h1 className="font-heading text-3xl uppercase tracking-tight">Confirming payment…</h1>
            <p className="text-slate-500 text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="relative inline-block mb-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-[#00f0ff]" />
              <Crown className="w-6 h-6 amber-text absolute -top-1 -right-1" />
            </div>
            <h1 className="font-heading text-4xl uppercase tracking-tight">You're <span className="cyan-text">Pro!</span></h1>
            <p className="text-slate-400 text-sm mt-2">
              Unlimited tabs, saving, export, mixer and share links are all unlocked.
            </p>
            <Link to="/">
              <Button className="mt-6 w-full bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-bold" data-testid="back-to-studio-button">
                Back to the Studio
              </Button>
            </Link>
          </>
        )}
        {state === "gift" && (
          <>
            <Gift className="w-16 h-16 mx-auto amber-text mb-4" />
            <h1 className="font-heading text-3xl uppercase tracking-tight">Your gift is <span className="amber-text">ready!</span></h1>
            <p className="text-slate-400 text-sm mt-2">Share this code with your drummer friend. They redeem it under "Have a gift code?".</p>
            <div className="mt-5 rounded-xl border border-[#ffab00]/30 bg-[#ffab00]/5 p-4">
              <div className="font-tab text-2xl tracking-widest amber-text" data-testid="gift-code-value">{giftCode}</div>
            </div>
            <Button
              onClick={() => { navigator.clipboard.writeText(giftCode); toast.success("Gift code copied"); }}
              className="mt-4 w-full bg-[#ffab00] text-black hover:bg-[#ffc233] font-bold"
              data-testid="copy-gift-code-button"
            >
              Copy gift code
            </Button>
            <Link to="/"><Button variant="outline" className="mt-2 w-full border-white/15 bg-white/5">Back to the Studio</Button></Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle className="w-14 h-14 mx-auto text-[#FF3366]/80 mb-4" />
            <h1 className="font-heading text-3xl uppercase tracking-tight">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than usual. Your plan will update automatically once it clears."
                : "We couldn't confirm this payment. If you were charged, it will reflect shortly."}
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-6 w-full border-white/15 bg-white/5" data-testid="back-to-studio-button">
                Back to the Studio
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
